import { cx } from '@orn/shared-web/utils/cx';
import * as React from 'react';

/**
 * MOPS-local button. Deliberately separate from
 * `@orn/shared-web/components/Button/Button` — the shared-web primitive ships
 * with `rounded-lg`, `shadow-xs`, and SaaS color treatments that cannot be
 * reconciled with the MOPS operator aesthetic (sharp edges, no shadows, dense
 * padding). Same decision as the MOPS-local `Table` and `Input`.
 *
 * Minimal API: three colors (primary / secondary / danger), three sizes, plus
 * `isLoading` / `isDisabled`. Colors map to single-accent/neutral/error from
 * the MOPS token palette.
 */

type ButtonColor = 'primary' | 'secondary' | 'danger';
type ButtonSize = 'xs' | 'sm' | 'md';

export interface IButtonProps extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'disabled'> {
  color?: ButtonColor;
  size?: ButtonSize;
  isLoading?: boolean;
  isDisabled?: boolean;
  children: React.ReactNode;
}

const COLORS: Record<ButtonColor, string> = {
  primary:
    'bg-fg-brand-primary text-fg-white border-fg-brand-primary hover:bg-fg-brand-primary_alt hover:border-fg-brand-primary_alt',
  secondary:
    'bg-bg-primary text-fg-primary border-border-primary hover:bg-bg-secondary hover:border-fg-brand-primary hover:text-fg-brand-primary',
  danger: 'bg-bg-primary text-fg-error-primary border-fg-error-primary hover:bg-fg-error-primary hover:text-fg-white',
};

const SIZES: Record<ButtonSize, string> = {
  xs: 'px-2 py-1 text-xs',
  sm: 'px-2.5 py-1.5 text-xs',
  md: 'px-3 py-2 text-sm',
};

const Button = ({
  color = 'primary',
  size = 'sm',
  isLoading = false,
  isDisabled = false,
  type = 'button',
  className,
  children,
  ...props
}: IButtonProps) => {
  const inert = isLoading || isDisabled;
  return (
    <button
      type={type}
      disabled={inert}
      aria-busy={isLoading || undefined}
      className={cx(
        'inline-flex cursor-pointer items-center justify-center gap-1.5 border font-medium uppercase tracking-wider transition-colors',
        'focus-visible:outline-border-brand focus-visible:outline-2 focus-visible:-outline-offset-1',
        'disabled:cursor-not-allowed disabled:opacity-50',
        SIZES[size],
        COLORS[color],
        className
      )}
      {...props}
    >
      {isLoading ? <Spinner /> : null}
      {children}
    </button>
  );
};

const Spinner = () => (
  <svg
    className="h-3 w-3 animate-spin"
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 16 16"
    fill="none"
    aria-hidden="true"
  >
    <circle cx="8" cy="8" r="6" stroke="currentColor" strokeWidth="2" opacity="0.25" />
    <path d="M14 8 a6 6 0 0 0 -6 -6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

export default Button;
